import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useProperty } from '../../context/PropertyContext';
import './ChatBot.css';

const WELCOME = {
  text: `Hi there! 👋 I'm the **Spaczo AI Assistant**.\n\nAsk me anything — finding rooms, listing property, payments, account issues, or anything else!\n\nWhat do you need help with?`,
  quickReplies: ['How to list a property?', 'How to find a room?', 'How to contact owner?', 'Is it free?', 'Edit my listing'],
  isBot: true
};

// Navigation shortcuts — handled without AI
const NAV_ACTIONS = {
  'contact support':   'support',
  'go to my listings': 'mylistings',
  'my listings':       'mylistings',
};

// Convert **bold** and \n to JSX
function renderText(text) {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    const parts = line.split(/\*\*(.*?)\*\*/g).map((part, j) =>
      j % 2 === 1 ? <strong key={j}>{part}</strong> : part
    );
    return <span key={i}>{parts}{i < lines.length - 1 && <br />}</span>;
  });
}

export default function ChatBot({ open, onClose, onUnread }) {
  const { setActiveTab } = useProperty();
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  // Reset chat on open
  useEffect(() => {
    if (open) {
      setMessages([WELCOME]);
      setInput('');
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const addMessage = (msg) => setMessages(prev => [...prev, msg]);

  const handleSend = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    const lower = msg.toLowerCase();

    // Navigation shortcuts
    if (NAV_ACTIONS[lower]) {
      addMessage({ text: msg, isBot: false });
      const tab   = NAV_ACTIONS[lower];
      const label = tab === 'support' ? 'Support page' : 'My Listings';
      setTimeout(() => {
        addMessage({ text: `Taking you to ${label} now...`, isBot: true, quickReplies: [] });
        setTimeout(() => { setActiveTab(tab); onClose?.(); }, 800);
      }, 300);
      return;
    }

    addMessage({ text: msg, isBot: false });
    setLoading(true);

    try {
      // Send message + last 8 messages as context (exclude welcome)
      const history = messages.slice(1).slice(-8);
      const res = await axios.post('/api/chat', { message: msg, history });
      const reply = res.data.reply;
      addMessage({
        text: reply,
        isBot: true,
        quickReplies: suggestQuickReplies(msg, reply)
      });
      if (!open) onUnread?.();
    } catch (err) {
      addMessage({
        text: err.response?.data?.message || `Sorry, I'm having trouble connecting right now. Please try again or visit the **Support** page.`,
        isBot: true,
        quickReplies: ['Contact support', 'Try again']
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  if (!open) return null;

  return (
    <div className="chatbot-window">
      {/* Header */}
      <div className="chatbot-header">
        <div className="chatbot-avatar">S</div>
        <div>
          <div className="chatbot-name">Spaczo AI Assistant</div>
          <div className="chatbot-status">
            <span className="chatbot-dot" /> Powered by Groq · Llama 3.1
          </div>
        </div>
        <button className="chatbot-close" onClick={onClose} aria-label="Close chat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="chatbot-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`chatbot-msg-wrap ${msg.isBot ? 'bot' : 'user'}`}>
            {msg.isBot && <div className="chatbot-msg-avatar">S</div>}
            <div className="chatbot-msg-col">
              <div className={`chatbot-bubble ${msg.isBot ? 'bot' : 'user'}`}>
                {renderText(msg.text)}
              </div>
              {msg.quickReplies?.length > 0 && (
                <div className="chatbot-quick-replies">
                  {msg.quickReplies.map((q, j) => (
                    <button key={j} className="chatbot-qr" onClick={() => handleSend(q)}>{q}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="chatbot-msg-wrap bot">
            <div className="chatbot-msg-avatar">S</div>
            <div className="chatbot-bubble bot chatbot-typing">
              <span /><span /><span />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="chatbot-input-row">
        <input
          ref={inputRef}
          className="chatbot-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Ask anything..."
          maxLength={500}
          disabled={loading}
        />
        <button
          className="chatbot-send"
          onClick={() => handleSend()}
          disabled={!input.trim() || loading}
          aria-label="Send"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

// Suggest contextual quick replies based on the conversation
function suggestQuickReplies(userMsg, botReply) {
  const t = (userMsg + ' ' + botReply).toLowerCase();
  if (t.includes('list') || t.includes('post'))
    return ['How to add photos?', 'Edit my listing', 'Contact support'];
  if (t.includes('find') || t.includes('search') || t.includes('room'))
    return ['How to contact owner?', 'Use map to find rooms', 'Is it free?'];
  if (t.includes('contact') || t.includes('phone') || t.includes('reveal'))
    return ['Why sign in required?', 'Report fake listing', 'Contact support'];
  if (t.includes('pay') || t.includes('price') || t.includes('cost') || t.includes('free'))
    return ['List a property', 'How does payment work?', 'Contact support'];
  if (t.includes('edit') || t.includes('update') || t.includes('change'))
    return ['Go to My Listings', 'Delete a listing', 'Contact support'];
  if (t.includes('delete') || t.includes('remove') || t.includes('rented'))
    return ['Go to My Listings', 'Pause listing instead', 'Contact support'];
  return ['Ask another question', 'Contact support'];
}
